// Cmd/Ctrl+Z → undo, Shift+Cmd/Ctrl+Z → redo, for a grid layout history.
// Window-level listener (mirrors the app's Ctrl+K pattern); bails when the
// event targets a text-editing surface so it never steals native text undo.

import * as React from 'react';

import type { Omittable } from '../../lib/props';

function isTextEditingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
  return !!el.isContentEditable;
}

export function useGridHistoryHotkeys(
  options: Omittable<{
    undo: () => void;
    redo: () => void;
    /** Gate the listener (e.g. on manage/edit mode). Defaults to true. */
    enabled?: boolean;
  }>,
) {
  const { undo, redo, enabled = true } = options;
  const actionsRef = React.useRef({ undo, redo });
  React.useLayoutEffect(() => {
    actionsRef.current = { undo, redo };
  });

  React.useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      if (e.isComposing || isTextEditingTarget(e.target)) return;
      e.preventDefault();
      if (e.shiftKey) actionsRef.current.redo();
      else actionsRef.current.undo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
